// Reads capture metadata (EXIF taken-at + GPS) from a JPEG or HEIC/HEIF
// file in the browser. The upload pipeline re-encodes images through
// <canvas> (and converts HEIC → JPEG), which strips EXIF — so this must
// run against the ORIGINAL File before compression, and the values
// travel to the server in the register call.
//
// Returns { taken_at, latitude, longitude } (each null when absent), or
// null when the file has no readable EXIF (PNG, screenshots…).
// taken_at is a zone-less ISO string ("2026-06-09T14:30:22") — EXIF
// times have no timezone, so the camera's wall-clock is kept as-is.
//
// HEIC support matters because iPhones shoot HEIC by default — those
// files used to fall through the JPEG check and lose their taken date
// (the "No date taken" bucket Larry K reported).

const SOI = 0xffd8
const APP1 = 0xffe1
const SOS = 0xffda

// Tag ids
const TAG_EXIF_IFD = 0x8769
const TAG_GPS_IFD = 0x8825
const TAG_DATETIME = 0x0132
const TAG_DATETIME_ORIGINAL = 0x9003
const TAG_MAKE = 0x010f
const TAG_MODEL = 0x0110
const TAG_GPS_LAT_REF = 0x0001
const TAG_GPS_LAT = 0x0002
const TAG_GPS_LNG_REF = 0x0003
const TAG_GPS_LNG = 0x0004
const TAG_GPS_ALT_REF = 0x0005
const TAG_GPS_ALT = 0x0006
const TAG_GPS_IMG_DIR_REF = 0x0010
const TAG_GPS_IMG_DIR = 0x0011
const TAG_ISO = 0x8827
const TAG_F_NUMBER = 0x829d
const TAG_EXPOSURE_TIME = 0x829a
const TAG_FOCAL_LENGTH = 0x920a
const TAG_FLASH = 0x9209
const TAG_LENS_MODEL = 0xa434

export async function readCaptureMetadata(file) {
  try {
    // EXIF lives in an APP1 segment near the start of the file (JPEG),
    // or in a `meta`-box item (HEIC/HEIF).
    const buffer = await file.slice(0, 256 * 1024).arrayBuffer()
    const view = new DataView(buffer)
    if (buffer.byteLength < 12) return null

    let meta = null
    if (view.getUint16(0) === SOI) {
      const tiffBase = findExifSegment(view)
      if (tiffBase === null) return null
      meta = parseTiff(view, tiffBase)
    } else if (view.getUint32(4) === 0x66747970 /* "ftyp" */) {
      meta = await parseHeif(file, view)
    }

    if (!meta) return null
    // Nothing worth keeping — no date, no position, no camera identity
    if (meta.taken_at === null && meta.latitude === null &&
        meta.camera_make === null && meta.camera_model === null) return null
    return meta
  } catch (_) {
    return null // truncated/odd files are simply treated as "no EXIF"
  }
}

// ── HEIC / HEIF (ISO-BMFF) ─────────────────────────────────────────
// The Exif payload is an item: `meta > iinf` names an item of type
// "Exif", `meta > iloc` says where its bytes live in the file. The
// payload itself is a 4-byte offset followed by the same TIFF block a
// JPEG carries, so it feeds straight into parseTiff.

async function parseHeif(file, headView) {
  const metaBox = findBox(headView, 0, headView.byteLength, "meta")
  if (!metaBox) return null // meta past the head slice — treat as no EXIF

  // `meta` is a FullBox — 4 bytes of version/flags before its children.
  const kids = { start: metaBox.start + 4, end: metaBox.end }

  const iinf = findBox(headView, kids.start, kids.end, "iinf")
  const iloc = findBox(headView, kids.start, kids.end, "iloc")
  if (!iinf || !iloc) return null

  const exifItemId = findExifItemId(headView, iinf)
  if (exifItemId === null) return null

  const extent = findItemExtent(headView, iloc, exifItemId)
  if (!extent || extent.length < 12 || extent.length > 4 * 1024 * 1024) return null

  // Fetch the exact payload range — it can sit beyond the head slice.
  const payload = new DataView(await file.slice(extent.offset, extent.offset + extent.length).arrayBuffer())
  if (payload.byteLength < 12) return null

  // 4-byte offset from the end of this field to the TIFF header
  // (usually 6, skipping "Exif\0\0").
  let tiffBase = 4 + payload.getUint32(0)
  if (!isTiffHeader(payload, tiffBase)) {
    // Some writers set the offset wrong — scan the first bytes instead.
    tiffBase = -1
    for (let i = 4; i + 4 <= Math.min(payload.byteLength, 64); i++) {
      if (isTiffHeader(payload, i)) { tiffBase = i; break }
    }
    if (tiffBase < 0) return null
  }
  return parseTiff(payload, tiffBase)
}

function isTiffHeader(view, off) {
  if (off < 0 || off + 4 > view.byteLength) return false
  const endian = view.getUint16(off)
  return (endian === 0x4949 || endian === 0x4d4d) &&
         view.getUint16(off + 2, endian === 0x4949) === 42
}

// Walks sibling boxes in [start, end) and returns the first with the
// given 4cc type as { start, end } of its PAYLOAD (after the header).
function findBox(view, start, end, type4cc) {
  const want = [...type4cc].reduce((a, c) => (a << 8) | c.charCodeAt(0), 0)
  let off = start
  while (off + 8 <= end && off + 8 <= view.byteLength) {
    let size = view.getUint32(off)
    const type = view.getUint32(off + 4)
    let header = 8
    if (size === 1) {
      if (off + 16 > view.byteLength) return null
      size = Number(view.getBigUint64(off + 8)) // 64-bit largesize
      header = 16
    } else if (size === 0) {
      size = end - off // box runs to the end of the enclosing space
    }
    if (size < header) return null
    if (type === want) {
      return { start: off + header, end: Math.min(off + size, view.byteLength) }
    }
    off += size
  }
  return null
}

// iinf → the item_ID whose infe entry has item_type "Exif".
function findExifItemId(view, iinf) {
  let off = iinf.start + 4 // FullBox version/flags
  const version = view.getUint8(iinf.start)
  off += version === 0 ? 2 : 4 // entry_count
  while (off + 8 <= iinf.end) {
    const size = view.getUint32(off)
    const type = view.getUint32(off + 4)
    if (size < 8) return null
    if (type === 0x696e6665 /* "infe" */) {
      const v = view.getUint8(off + 8)
      if (v >= 2) {
        const idSize = v === 2 ? 2 : 4
        const itemId = v === 2 ? view.getUint16(off + 12) : view.getUint32(off + 12)
        const itemType = view.getUint32(off + 12 + idSize + 2) // skip protection_index
        if (itemType === 0x45786966 /* "Exif" */) return itemId
      }
    }
    off += size
  }
  return null
}

// iloc → absolute file offset + length of the item's first extent.
function findItemExtent(view, iloc, wantItemId) {
  let off = iloc.start
  const version = view.getUint8(off)
  off += 4 // version/flags
  const sizes = view.getUint8(off)
  const offsetSize = sizes >> 4
  const lengthSize = sizes & 0xf
  const sizes2 = view.getUint8(off + 1)
  const baseOffsetSize = sizes2 >> 4
  const indexSize = version === 1 || version === 2 ? (sizes2 & 0xf) : 0
  off += 2
  let itemCount
  if (version < 2) { itemCount = view.getUint16(off); off += 2 }
  else { itemCount = view.getUint32(off); off += 4 }

  for (let i = 0; i < itemCount; i++) {
    let itemId
    if (version < 2) { itemId = view.getUint16(off); off += 2 }
    else { itemId = view.getUint32(off); off += 4 }
    let constructionMethod = 0
    if (version === 1 || version === 2) {
      constructionMethod = view.getUint16(off) & 0xf
      off += 2
    }
    off += 2 // data_reference_index
    const baseOffset = readUint(view, off, baseOffsetSize); off += baseOffsetSize
    const extentCount = view.getUint16(off); off += 2
    let first = null
    for (let e = 0; e < extentCount; e++) {
      off += indexSize
      const extOffset = readUint(view, off, offsetSize); off += offsetSize
      const extLength = readUint(view, off, lengthSize); off += lengthSize
      if (e === 0) first = { offset: baseOffset + extOffset, length: extLength }
    }
    if (itemId === wantItemId) {
      // Only construction_method 0 (absolute file offsets) is supported —
      // idat-relative Exif items are vanishingly rare in camera files.
      return constructionMethod === 0 ? first : null
    }
  }
  return null
}

function readUint(view, off, size) {
  if (size === 0) return 0
  if (size === 4) return view.getUint32(off)
  if (size === 8) return Number(view.getBigUint64(off))
  if (size === 2) return view.getUint16(off)
  return view.getUint8(off)
}

// Walk JPEG segments looking for APP1 "Exif\0\0"; returns the TIFF
// header offset or null.
function findExifSegment(view) {
  let offset = 2
  while (offset + 4 <= view.byteLength) {
    const marker = view.getUint16(offset)
    if ((marker & 0xff00) !== 0xff00 || marker === SOS) return null
    const size = view.getUint16(offset + 2)
    if (marker === APP1 && offset + 10 <= view.byteLength &&
        view.getUint32(offset + 4) === 0x45786966 /* "Exif" */ &&
        view.getUint16(offset + 8) === 0) {
      return offset + 10
    }
    offset += 2 + size
  }
  return null
}

function parseTiff(view, base) {
  const endian = view.getUint16(base)
  const little = endian === 0x4949 // "II"
  if (!little && endian !== 0x4d4d) return null
  if (view.getUint16(base + 2, little) !== 42) return null

  const ifd0 = base + view.getUint32(base + 4, little)
  const tiff = { view, base, little }

  const ifd0Tags = readIfd(tiff, ifd0)
  const exifTags = ifd0Tags[TAG_EXIF_IFD] ? readIfd(tiff, base + ifd0Tags[TAG_EXIF_IFD].value) : {}
  const gpsTags = ifd0Tags[TAG_GPS_IFD] ? readIfd(tiff, base + ifd0Tags[TAG_GPS_IFD].value) : {}

  const dateEntry = exifTags[TAG_DATETIME_ORIGINAL] || ifd0Tags[TAG_DATETIME]
  const taken_at = dateEntry ? parseExifDate(readAscii(tiff, dateEntry)) : null

  let latitude = null
  let longitude = null
  if (gpsTags[TAG_GPS_LAT] && gpsTags[TAG_GPS_LNG]) {
    const lat = readCoordinate(tiff, gpsTags[TAG_GPS_LAT], gpsTags[TAG_GPS_LAT_REF], "S", 90)
    const lng = readCoordinate(tiff, gpsTags[TAG_GPS_LNG], gpsTags[TAG_GPS_LNG_REF], "W", 180)
    // 0,0 ("null island") means the GPS chip never got a fix
    if (lat !== null && lng !== null && !(lat === 0 && lng === 0)) {
      latitude = lat
      longitude = lng
    }
  }

  // Camera identity (IFD0)
  const camera_make = ifd0Tags[TAG_MAKE] ? cleanAscii(readAscii(tiff, ifd0Tags[TAG_MAKE])) : null
  const camera_model = ifd0Tags[TAG_MODEL] ? cleanAscii(readAscii(tiff, ifd0Tags[TAG_MODEL])) : null

  // Altitude: RATIONAL meters + ref byte (1 = below sea level)
  let altitude = null
  if (gpsTags[TAG_GPS_ALT]) {
    const alt = readRational(tiff, gpsTags[TAG_GPS_ALT])
    if (alt !== null && isFinite(alt) && Math.abs(alt) <= 12000) {
      const below = gpsTags[TAG_GPS_ALT_REF] && (gpsTags[TAG_GPS_ALT_REF].value & 0xff) === 1
      altitude = Math.round((below ? -alt : alt) * 100) / 100
    }
  }

  // Direction the camera faced: RATIONAL 0-359.99 ('T'rue or 'M'agnetic north)
  let heading = null
  if (gpsTags[TAG_GPS_IMG_DIR]) {
    const dir = readRational(tiff, gpsTags[TAG_GPS_IMG_DIR])
    if (dir !== null && dir >= 0 && dir < 360) heading = Math.round(dir * 100) / 100
  }

  // Camera settings — kept as short strings, exact keys the server whitelists
  const exif = {}
  if (exifTags[TAG_ISO]) {
    const iso = readShort(tiff, exifTags[TAG_ISO])
    if (iso > 0) exif.iso = String(iso)
  }
  if (exifTags[TAG_F_NUMBER]) {
    const f = readRational(tiff, exifTags[TAG_F_NUMBER])
    if (f !== null && f > 0) exif.f_number = String(Math.round(f * 10) / 10)
  }
  if (exifTags[TAG_EXPOSURE_TIME]) {
    const et = readRationalParts(tiff, exifTags[TAG_EXPOSURE_TIME])
    if (et && et.den > 0 && et.num > 0) {
      exif.exposure_time = et.num < et.den ? `${et.num}/${et.den}` : String(Math.round((et.num / et.den) * 100) / 100)
    }
  }
  if (exifTags[TAG_FOCAL_LENGTH]) {
    const fl = readRational(tiff, exifTags[TAG_FOCAL_LENGTH])
    if (fl !== null && fl > 0) exif.focal_length = String(Math.round(fl * 10) / 10)
  }
  if (exifTags[TAG_FLASH]) {
    // Bit 0 of the flash code = "flash fired"
    exif.flash = (readShort(tiff, exifTags[TAG_FLASH]) & 1) === 1 ? "fired" : "off"
  }
  if (exifTags[TAG_LENS_MODEL]) {
    const lens = cleanAscii(readAscii(tiff, exifTags[TAG_LENS_MODEL]))
    if (lens) exif.lens_model = lens
  }

  return {
    taken_at, latitude, longitude,
    camera_make, camera_model, altitude, heading,
    exif: Object.keys(exif).length > 0 ? exif : null
  }
}

// Trims NULs/whitespace and caps length; returns null when empty
function cleanAscii(str) {
  const s = (str || "").replace(/\0/g, "").trim().slice(0, 64)
  return s || null
}

// First RATIONAL of an entry as a float, or null
function readRational(tiff, entry) {
  const parts = readRationalParts(tiff, entry)
  if (!parts || parts.den === 0) return null
  return parts.num / parts.den
}

function readRationalParts({ view, base, little }, entry) {
  if (entry.type !== 5 || entry.count < 1) return null
  const start = base + entry.value
  if (start + 8 > view.byteLength) return null
  return { num: view.getUint32(start, little), den: view.getUint32(start + 4, little) }
}

// Inline SHORT value (stored in the first 2 bytes of the value slot)
function readShort({ view, little }, entry) {
  return view.getUint16(entry.slot, little)
}

// Reads an IFD into { tag: { type, count, value, valueOffset } }.
// `value` is the inline 32-bit slot; larger payloads live at valueOffset.
function readIfd({ view, little }, offset) {
  const tags = {}
  if (offset + 2 > view.byteLength) return tags
  const count = view.getUint16(offset, little)
  for (let i = 0; i < count; i++) {
    const entry = offset + 2 + i * 12
    if (entry + 12 > view.byteLength) break
    const tag = view.getUint16(entry, little)
    tags[tag] = {
      type: view.getUint16(entry + 2, little),
      count: view.getUint32(entry + 4, little),
      value: view.getUint32(entry + 8, little),
      slot: entry + 8
    }
  }
  return tags
}

function readAscii({ view, base, little }, entry) {
  const length = entry.count
  // Values ≤4 bytes are stored inline in the value slot
  const start = length <= 4 ? entry.slot : base + entry.value
  let str = ""
  for (let i = 0; i < length && start + i < view.byteLength; i++) {
    const c = view.getUint8(start + i)
    if (c === 0) break
    str += String.fromCharCode(c)
  }
  return str
}

// "2026:06:09 14:30:22" → "2026-06-09T14:30:22"
function parseExifDate(str) {
  const m = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(str || "")
  if (!m) return null
  if (parseInt(m[1]) <= 1970) return null // unset camera clocks
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}`
}

// GPS coordinates are three RATIONALs (deg, min, sec) + an N/S/E/W ref
function readCoordinate(tiff, valueEntry, refEntry, negativeRef, maxAbs) {
  const { view, base, little } = tiff
  if (valueEntry.type !== 5 || valueEntry.count < 1) return null

  const start = base + valueEntry.value
  const parts = []
  for (let i = 0; i < Math.min(valueEntry.count, 3); i++) {
    const num = view.getUint32(start + i * 8, little)
    const den = view.getUint32(start + i * 8 + 4, little)
    parts.push(den === 0 ? num : num / den)
  }
  if (parts.length === 0 || !isFinite(parts[0])) return null

  let value = parts[0] + (parts[1] || 0) / 60 + (parts[2] || 0) / 3600
  const ref = refEntry ? readAscii(tiff, refEntry) : ""
  if (ref.toUpperCase().startsWith(negativeRef)) value = -value
  if (!isFinite(value) || Math.abs(value) > maxAbs) return null

  return Math.round(value * 1e7) / 1e7
}
